import { Card } from "@components/common/card"
import { Separator } from "@components/common/separator"
import { Badge } from "@components/common/badge"
import { cn, getContrastTextColor } from "@pma/utils/helpers"
import axios from "axios"
import { useMutation, useQuery } from "@tanstack/react-query"
import { SERVER_URL, STATUS_DICTIONARY_ENDPOINT } from "@pma/utils/api-routes"
import { useAuth } from "@stores/auth-store"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { DictType } from "@pma/types"
import AddStatus from "./add-status-modal"
import { Pencil, X } from "lucide-react"
import { toast } from "@hooks/use-toast"
import PmsLoader from "@components/common/loader"
import useTranslator from "@hooks/use-translator"
import SimpleHoverWithActionButtons from "../hover-action-buttons/SimpleHoverWithActionButtons"
import UpdateStatus from "./update-status-modal"
import { useState } from "react"

const TaskStatusOptions = () => {
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const { authConfig } = useAuth()
  const { translate } = useTranslator()

  const [selectedStatus, setSelectedStatus] = useState<DictType>({
    name: "",
    color: "#6465ca",
  })

  const userId = authConfig?.id
  const orgId = authConfig?.last_organization_id
  const getTaskStatus = async () => {
    const response = await axios.get(
      SERVER_URL +
        STATUS_DICTIONARY_ENDPOINT +
        `?context_type=organization%20user&context_id=${orgId}&for_task=true`,
      {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["task-status"],
    queryFn: getTaskStatus,
  })

  const deleteStatus = async (id: number) => {
    const response = await axios.delete(SERVER_URL + STATUS_DICTIONARY_ENDPOINT + `/delete/${id}`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response?.data
  }

  const deleteStatusMutation = useMutation({
    mutationKey: ["delete-status"],
    mutationFn: (id: number) => deleteStatus(id),
    onSuccess: () => {
      refetch()
      toast({
        title: translate("Success"),
        description: translate("Task status updated"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to delete status"),
        variant: "error",
      })
    },
  })

  const updateStatusApiCall = async (payload: DictType) => {
    const ENDPOINT = STATUS_DICTIONARY_ENDPOINT + "/edit"
    const response = await axios.post(SERVER_URL + ENDPOINT, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response?.data
  }

  const updateStatusMutation = useMutation({
    mutationKey: ["update-status", "task"],
    mutationFn: (payload: DictType) => updateStatusApiCall(payload),
    onSuccess: (data) => {
      if (refetch) refetch()
      toast({
        title: translate("Success"),
        description: translate("Status updated successfully"),
        variant: "success",
      })
      setSelectedStatus({
        name: "",
        color: "#6465ca",
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Error updating status"),
        variant: "error",
      })
    },
  })

  const handleUpdate = () => {
    if (!selectedStatus?.name) {
      return toast({
        title: translate("Info"),
        description: translate("Enter status title"),
        variant: "info",
      })
    }

    const payload = {
      name: selectedStatus.name,
      color: selectedStatus.color,
      canceled: selectedStatus.canceled ?? false,
      finished: selectedStatus.finished ?? false,
      for_task: true,
      for_project: false,
      status_id: selectedStatus.id,
    }

    return updateStatusMutation.mutate({
      ...payload,
    })
  }

  return (
    <Card className='min-h-[200px] p-3'>
      <div className='flex items-center justify-between'>
        <h3 className='tex-lg font-semibold'>{translate("Tasks Status")}</h3>
        <AddStatus option='task' revalidate={refetch} />
      </div>

      <Separator className='my-2' />
      {isLoading && <PmsLoader />}

      <div className='mt-4 flex flex-wrap gap-3'>
        {data?.map((status: DictType) => (
          <SimpleHoverWithActionButtons
            key={status?.id}
            actionLoading={deleteStatusMutation.isPending || updateStatusMutation.isPending}
            actionButtons={[
              {
                children: <Pencil />,
                onClick: () => setSelectedStatus(status),
                popoverContent: (
                  <UpdateStatus option='task' data={selectedStatus} setStatus={setSelectedStatus} />
                ),
                popoverFooterButtons: [
                  {
                    label: translate("Update"),
                    variant: "default",
                    onClick: handleUpdate,
                  },
                ],
              },
              {
                children: <X />,
                onClick: () => deleteStatusMutation.mutate(status?.id),
                className: "text-red-500",
              },
            ]}
          >
            <Badge
              className={cn("relative cursor-pointer")}
              style={{
                background: status?.color,
                color: getContrastTextColor(status?.color || "#6465ca"),
              }}
            >
              {status?.name}
            </Badge>
          </SimpleHoverWithActionButtons>
        ))}
      </div>
    </Card>
  )
}

export default TaskStatusOptions
